import { useId } from "react"
import { Button, Dialog, Stack, Typography } from "@octopus-energy/coral/atoms"
import { styled } from "@octopus-energy/coral-design/jsx"
import { IconArrowRotateAntiClockwise } from "@octopus-energy/icons"
import { useIsOpen } from "@oefr/hooks"
import { useTranslations } from "next-intl"
import { getPartnerConfig, type Partner } from "@/features/partners"
import { navigatorRoutesPaths } from "@/routes/navigator"

/**
 * Lets a seller purge the current quote session and go back to the Navigator
 * portal, so that no data from the previous customer leaks into the next quote.
 *
 * The purge itself is done server-side by `/navigator/reinitialisation`, which
 * reuses `onboardingWizard.deleteCookieOnServer` — the same mechanism already
 * used at the end of the flow.
 *
 * The confirmation submits a native POST form rather than following a link, so
 * that a prefetch or an accidental GET can never destroy a live session.
 */
type ResetSessionButtonProps = {
    partnerSlug: Partner | null
    affiliateCode?: string | null
}

export function ResetSessionButton({ partnerSlug, affiliateCode }: ResetSessionButtonProps) {
    const t = useTranslations("ResetSessionButton")

    // Mirrors the destination chosen by `getPartnerEntryPath`.
    const { name: partnerName, portail } = getPartnerConfig(partnerSlug)
    const description = !partnerSlug
        ? t("confirmation.description.portal")
        : portail
          ? t("confirmation.description.partnerPortal", { partnerName })
          : t("confirmation.description.partnerFunnel", { partnerName })

    const confirmation = useIsOpen()
    const titleId = useId()

    return (
        <>
            <Stack justifyContent="center">
                <Button
                    size="small"
                    variant="ghost"
                    color="base100"
                    startIcon={<IconArrowRotateAntiClockwise size={16} />}
                    onClick={confirmation.open}
                    attributes={{ "data-testid": "reset-session-button" }}
                >
                    {t("callToAction")}
                </Button>
            </Stack>
            <Dialog
                ariaLabelledBy={titleId}
                open={confirmation.isOpen}
                onClose={confirmation.close}
            >
                <Stack
                    flexDirection="column"
                    gap="lg"
                >
                    <Typography
                        textStyle="h4"
                        component="p"
                        attributes={{ id: titleId }}
                    >
                        {t("confirmation.title")}
                    </Typography>
                    <Typography>{description}</Typography>
                    <Stack
                        flexDirection={{ base: "column", sm: "row" }}
                        gap="sm"
                    >
                        <Button
                            size="small"
                            color="base100"
                            variant="outlined"
                            onClick={confirmation.close}
                        >
                            {t("confirmation.cancel")}
                        </Button>
                        <styled.form
                            width="full"
                            method="post"
                            // On the confirmation step the cookie is already purged, so the
                            // destination is passed along.
                            action={navigatorRoutesPaths.resetSession.resolve({
                                queryParams: { partnerSlug, affiliate: affiliateCode },
                            })}
                        >
                            <Button
                                fullWidth
                                size="small"
                                type="submit"
                                attributes={{ "data-testid": "reset-session-confirm" }}
                            >
                                {t("confirmation.confirm")}
                            </Button>
                        </styled.form>
                    </Stack>
                </Stack>
            </Dialog>
        </>
    )
}

ResetSessionButton.messages = ["ResetSessionButton"] as const
